import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import { EnquiryAudienceEnum } from 'generated/graphql';

import { useEnquiryModal } from './use-enquiry-modal';
import { useEnquiryVisibility } from './use-enquiry-visibility';
import {
    TILE_ENQUIRY_CONTEXT_API_ROUTE,
    type TileEnquiryContextResponse,
} from '~/routes/remix-api.tile-enquiry-context';

export interface TileEnquiryConfig {
    /**
     * The tile's item type — product / professional / project tiles enquire
     * (a project enquiry goes to the project's professional).
     */
    itemType: 'product' | 'professional' | 'project';
    /** The Library Item id the enquiry is about. */
    siteTreeID: number;
    /** The professional's id (available on the tile) for the instant modal. */
    professionalId: number;
    /** The professional's name (available on the tile) for the instant modal. */
    professionalName: string;
    /** The professional's Link, used to lazily enrich the enquiry context. */
    professionalLink: string;
    /** Item name shown in the modal header. */
    itemTitle?: string;
    /** Item thumbnail shown in the modal header. */
    itemImage?: string;
}

/**
 * Opens the standard enquiry modal for a listing tile (BC-4810).
 *
 * The modal opens **instantly** on click using the professional id/name already
 * on the tile, then a background fetch (`/remix-api/tile-enquiry-context`)
 * enriches it with the company logo and custom message (the modal header shows a
 * skeleton until it lands). Auth gating happens on submit, per the common
 * enquiry pattern.
 *
 * Listing tiles don't carry the professional's `EnquiryAudience`, so the enquiry
 * action is offered optimistically and withdrawn (`showEnquire`) once the fetch
 * reveals a professional who doesn't take enquiries from this visitor. Callers
 * should `prefetch()` on hover / sheet open so that resolves before the click.
 *
 * Call it from a component that outlives the action surface — the modal is
 * enriched (and withdrawn) by *this* instance after the click, so a hook that
 * unmounts with, say, a closing action sheet takes the pending work with it.
 * `config` may be null for tiles with no enquiry target.
 */
export const useTileEnquiry = (config: TileEnquiryConfig | null) => {
    // Destructured to primitives: callers build `config` inline, so depending on
    // its identity would rebuild the modal context on every render.
    const {
        itemType,
        siteTreeID,
        professionalId,
        professionalName,
        professionalLink,
        itemTitle,
        itemImage,
    } = config ?? ({} as Partial<TileEnquiryConfig>);
    const fetcher = useFetcher<TileEnquiryContextResponse>();
    const fetched = fetcher.data?.professional ?? null;
    const openedRef = useRef(false);
    const enrichedRef = useRef(false);

    // The loader always answers with a body, so `data` normally marks the end of
    // the lookup — found or not. A transport-level failure resolves nothing at
    // all though, so treat the load returning to idle as an ending too, or the
    // modal's header skeleton would shimmer forever.
    const [loadFinished, setLoadFinished] = useState(false);
    const loadingRef = useRef(false);
    useEffect(() => {
        if (fetcher.state !== 'idle') {
            loadingRef.current = true;
            return;
        }
        if (loadingRef.current) {
            loadingRef.current = false;
            setLoadFinished(true);
        }
    }, [fetcher.state]);
    const settled = fetcher.data !== undefined || loadFinished;

    const enquiryData = useMemo(() => {
        if (!itemType || siteTreeID === undefined) {
            return undefined;
        }
        return {
            professional: {
                ID: professionalId ?? 0,
                Title: professionalName ?? '',
                branchOptions: [],
                CompanyLogo: fetched?.CompanyLogo ?? '',
                LogoBackgroundColor: fetched?.LogoBackgroundColor ?? 'white',
                CustomEnquiryMessage:
                    fetched?.CustomEnquiryMessage ?? undefined,
                EnquiryMessageOverrideEnabled:
                    fetched?.EnquiryMessageOverrideEnabled ?? false,
                EnquiryMessagePlaceholder:
                    fetched?.EnquiryMessagePlaceholder ?? undefined,
            },
            itemType,
            siteTreeID,
            ctaLabel: 'Enquire',
            includeBranch: false,
            itemTitle,
            itemImage,
            logoPending: !settled,
        };
    }, [
        fetched,
        settled,
        professionalId,
        professionalName,
        itemType,
        siteTreeID,
        itemTitle,
        itemImage,
    ]);

    // Every tile in a grid mounts this hook, so opt out of the `enquire` URL
    // param auto-open — that belongs to the detail-page CTA that set it.
    const {
        isOpen,
        context,
        openEnquiryModal,
        refreshEnquiryModalContext,
        closeEnquiryModal,
    } = useEnquiryModal(enquiryData, { autoOpenFromUrl: false });

    // The modal is global and every tile owns an instance of this hook, so a
    // late response must only touch it while it still shows *this* tile's item.
    // Otherwise closing one tile's modal and opening another's lets the first
    // fetch swap the enquiry target, or dismiss the modal outright.
    const ownsOpenModal =
        isOpen &&
        !!enquiryData &&
        context?.itemType === enquiryData.itemType &&
        context?.siteTreeID === enquiryData.siteTreeID;

    // Assume enquiries are open until the fetch says otherwise, so the button
    // isn't held back on the common path.
    const { showEnquiryCTA } = useEnquiryVisibility({
        EnquiryAudience:
            fetched?.EnquiryAudience ?? EnquiryAudienceEnum.Anybody,
    });
    const showEnquire = !!enquiryData && (!settled || showEnquiryCTA);

    const prefetch = useCallback(() => {
        if (!professionalLink || fetcher.state !== 'idle' || fetcher.data) {
            return;
        }
        fetcher.load(
            `${TILE_ENQUIRY_CONTEXT_API_ROUTE}?link=${encodeURIComponent(
                professionalLink,
            )}`,
        );
    }, [fetcher, professionalLink]);

    // Settle the (still-open) modal once the background fetch resolves: either
    // swap the skeleton for the real logo + custom message, or back out because
    // this professional doesn't accept the enquiry. Guard with a ref so the
    // changing `refreshEnquiryModalContext` identity can't loop.
    useEffect(() => {
        if (
            !openedRef.current ||
            enrichedRef.current ||
            !settled ||
            !ownsOpenModal
        ) {
            return;
        }
        enrichedRef.current = true;
        if (!showEnquiryCTA) {
            closeEnquiryModal();
            return;
        }
        refreshEnquiryModalContext();
    }, [
        settled,
        ownsOpenModal,
        showEnquiryCTA,
        refreshEnquiryModalContext,
        closeEnquiryModal,
    ]);

    const onEnquire = useCallback(() => {
        if (!enquiryData || (settled && !showEnquiryCTA)) {
            return;
        }
        openedRef.current = true;
        // Open immediately with the tile-derived context.
        openEnquiryModal({ ctaLabel: 'Enquire' });
        // Enrich in the background (no-op if hover already resolved it).
        prefetch();
    }, [enquiryData, settled, showEnquiryCTA, openEnquiryModal, prefetch]);

    return {
        onEnquire,
        /** Resolve the enquiry context ahead of the click (hover / sheet open). */
        prefetch,
        /** False once the fetch shows this visitor can't enquire — hide the action. */
        showEnquire,
    };
};
